import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Trans } from 'react-i18next'
import { useHistory } from 'react-router'
import { Formik } from 'formik'
import { filter, forEach, includes, map, sortBy } from 'lodash'

import { Format, OutputFormat } from 'services/Dates'
import { getUserName, getPositionName } from 'services/Utilities'
import { createErrorNotification } from 'store/actions/notifications'
import { closeModal } from 'store/actions/modals'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Checkbox, Form } from 'components/Forms'
import { Loading } from 'components/UI'
import { Alert, Button as MerinioButton, Card, Checkbox as MerinioCheckbox, List } from 'ui'
import useApi from 'components/Hooks/useApi'

import { t } from 'translations'

type Props = {
  events: (MData.Event & { punchCardId?: string; id: string })[]
}

const ScheduleDeleteModal: FC<Props> = ({ events }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const hasRecurrence = events.some(event => event.rruleId)
  const hasPublished = events.some(event => event.published && event.employeeId)
  const hasAssociatedPunchards = events.some(event => event.punchCardId)

  const eventsWithoutPunchCard = sortBy(
    filter(events, event => !event.punchCardId),
    ['employeeId']
  )
  const allEventsSorted = sortBy(events, ['punchCardId', 'employeeId'])

  const initialSelectedEvents: { [key: string]: boolean } = eventsWithoutPunchCard.reduce((acc, { id }) => ({ ...acc, [id]: true }), {})

  const [selectedEvents, setSelectedEvents] = useState(initialSelectedEvents)
  const [deleteEventsWithPunchCard, setDeleteEventsWithPunchCard] = useState(false)

  const eventsIdsToDelete = filter(Object.keys(selectedEvents), key => selectedEvents[key])
  const someEventsToDeletehavePunchCard = filter(events, ({ id }) => includes(eventsIdsToDelete, id)).some(event => event.punchCardId)
  // Query definition
  const { handleApi: deleteEvents } = useApi({ method: 'DELETE', url: '/schedule/schedule' })

  const onSubmit = async ({ sendCancellationEmails, deleteFutureRecurrence }) => {
    const { isError } = await deleteEvents({ ids: eventsIdsToDelete, sendCancellationEmails, deleteFutureRecurrence })
    if (isError) return dispatch(createErrorNotification())
    dispatch(closeModal())
  }

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    setDeleteEventsWithPunchCard(!deleteEventsWithPunchCard)
    const eventsIdsWithPunchCard = map(filter(events, 'punchCardId'), 'id')
    const eventsToSelect: { [key: string]: boolean } = eventsIdsWithPunchCard.reduce((acc, id) => ({ ...acc, [id]: true }), {})
    if (target.checked) setSelectedEvents({ ...selectedEvents, ...eventsToSelect })
    else {
      const newSelectedEvents = forEach(selectedEvents, (_value, key) => {
        if (includes(eventsIdsWithPunchCard, key)) selectedEvents[key] = false
      })
      setSelectedEvents(newSelectedEvents)
    }
  }

  type EventSummaryListWithRadioButtonsProps = {
    events: (MData.Event & { punchCardId?: string; id: string })[]
    isTime?: boolean
    outFormat?: OutputFormat
    showAssociatedPunchard?: boolean
  }
  const EventSummaryListWithRadioButtons = ({ events, ...props }: EventSummaryListWithRadioButtonsProps) => {
    const { isTime, outFormat, showAssociatedPunchard } = props

    if (!events.length) return null

    return (
      <Card maxHeight="30rem" className="mb-1">
        <List className="list-group-flush">
          {events.map(({ id, employeeId, start, end, positionId, punchCardId }) => {
            const shiftHasPunchCard = punchCardId && showAssociatedPunchard
            const goToPunchCard = () => {
              history.push(`/manage/punch/cards/${punchCardId}`)
              dispatch(closeModal())
            }
            return (
              <List.Item className="d-flex" key={id}>
                <div>
                  <div className="d-flex flex-wrap" style={{ whiteSpace: 'pre-wrap' }}>
                    <Trans t={t} i18nKey={`manager.schedule.EVENT_SUMMARY`}>
                      <b>{{ name: employeeId ? getUserName(employeeId) : t('manager.schedule.UNASSIGNED_SHIFT') }}</b>
                      <b>
                        {{ start: !isTime ? Format(start, { outFormat: outFormat || 'humanDateTime' }) : start }}
                        {{ end: !isTime ? Format(end, { outFormat: outFormat || 'humanDateTime' }) : end }}
                      </b>
                      {{ positionName: getPositionName(positionId) }}
                    </Trans>
                  </div>
                  {!!shiftHasPunchCard && (
                    <div className="d-flex flex-row align-items-baseline text-danger ml-2">
                      <i className="fad fa-chess-clock" />
                      <MerinioButton className="ml-2" isLink color="error" type="button" onClick={goToPunchCard}>
                        {t(`manager.schedule.SEE_PUNCHCARD`)}
                      </MerinioButton>
                    </div>
                  )}
                </div>
                <div className="ml-auto">
                  <MerinioButton
                    size="small"
                    className={`${selectedEvents[id] ? 'btn-success' : 'btn-outline-secondary'}`}
                    color={selectedEvents[id] ? 'success' : 'secondary'}
                    outlined={!selectedEvents[id]}
                    onClick={() => setSelectedEvents({ ...selectedEvents, [id]: !selectedEvents[id] })}
                  >
                    <i className={`${selectedEvents[id] ? 'fas fa-check-circle' : 'far fa-circle'}`} />
                  </MerinioButton>
                </div>
              </List.Item>
            )
          })}
        </List>
      </Card>
    )
  }

  if (!events) return <Loading />

  return (
    <BaseModal hideButtons title={`manager.schedule.DELETE_MODAL`}>
      <Formik
        enableReinitialize={true}
        initialValues={{ sendCancellationEmails: hasPublished || someEventsToDeletehavePunchCard, deleteFutureRecurrence: false }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values }) => {
          const showOptions = hasPublished || someEventsToDeletehavePunchCard || hasRecurrence
          return (
            <Form className="mt-2" noPrompt>
              <Alert variant="warning" className="my-2" noIcon>
                <div>
                  <i className={`fas fa-exclamation-triangle mr-2`} />
                  {t(`manager.schedule.DELETE_MODAL.DESCRIPTION`)}
                </div>
                {values.sendCancellationEmails ? (
                  <div>
                    <i className={`fas fa-exclamation-triangle mr-2`} />
                    {t('manager.schedule.SEND_CANCELLATION_NOTICE')}
                  </div>
                ) : null}
              </Alert>
              {!!hasAssociatedPunchards && (
                <>
                  <Alert variant="danger" className="my-2" noIcon>
                    <div>
                      <i className={`fas fa-exclamation-circle mr-2`} />
                      {t(`manager.schedule.DELETE_MODAL.PUNCHCARDS_ASSOCIATED`)}
                    </div>
                  </Alert>
                  <Card className="mb-1">
                    <Card.Body className="bg-light">
                      <div className="form-check">
                        <MerinioCheckbox id="deleteEventsWithPunchCard" checked={deleteEventsWithPunchCard} onChange={handleChange} />
                        <label className="form-check-label" htmlFor="deleteEventsWithPunchCard">
                          {t('manager.schedule.DELETE_MODAL.DELETEEVENTSWITHPUNCHCARDS')}
                        </label>
                      </div>
                    </Card.Body>
                  </Card>
                </>
              )}

              <EventSummaryListWithRadioButtons events={deleteEventsWithPunchCard ? allEventsSorted : eventsWithoutPunchCard} showAssociatedPunchard />
              {showOptions ? (
                <Card className="bg-light mt-2">
                  <Card.Body>
                    {/* Whether or not to delete future recurrence */}
                    {hasRecurrence ? <Checkbox module="manager.schedule.DELETE_MODAL" name="deleteFutureRecurrence" /> : null}
                    {/* Whether or not to send a cancellation email */}
                    {hasPublished || someEventsToDeletehavePunchCard ? <Checkbox module="manager.schedule.DELETE_MODAL" name="sendCancellationEmails" /> : null}
                  </Card.Body>
                </Card>
              ) : null}
              <div className="mt-2">
                <Button
                  className="btn btn-primary mr-2"
                  type="submit"
                  isSubmitting={isSubmitting}
                  text="actions.CONFIRM"
                  disabled={!eventsIdsToDelete.length}
                />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ScheduleDeleteModal
